/**
 * WORLD-CUP-INDIA-DOMINATION-P1 — IN metrics aggregator.
 * Mirrors the Brasil architecture but only counts events whose payload
 * is attributed to country === "IN". No DB writes, no synthetic data.
 */
type Listener = () => void;

interface InfluencerCounts { total: number; today: number; shares: number; referrals: number; predictions: number; views: number }
interface FeedEntry { id: string; at: number; kind: "share" | "join" | "predict" | "community"; message: string }
interface ShareItem { kind: "prediction" | "ranking" | "match" | "influencer"; count: number; label: string }

const influencerMap = new Map<string, InfluencerCounts>();
const feed: FeedEntry[] = [];
const shareCounts: Record<ShareItem["kind"], ShareItem> = {
  prediction: { kind: "prediction", count: 0, label: "Predictions shared" },
  ranking:    { kind: "ranking",    count: 0, label: "Rankings shared" },
  match:      { kind: "match",      count: 0, label: "Match cards shared" },
  influencer: { kind: "influencer", count: 0, label: "Influencers shared" },
};

let communityTotal = 0;
let communityToday = 0;
let communityWeek = 0;
let predictionsTotal = 0;
let sharesTotal = 0;
let referralsTotal = 0;

const listeners = new Set<Listener>();
let initialized = false;
let lastFeedAt = 0;
const FEED_MIN_GAP_MS = 1500;
const FEED_MAX = 25;

function notify() { listeners.forEach((l) => { try { l(); } catch { /* noop */ } }); }

function bumpInfluencer(slug: string, patch: Partial<InfluencerCounts>) {
  const cur = influencerMap.get(slug) ?? { total: 0, today: 0, shares: 0, referrals: 0, predictions: 0, views: 0 };
  influencerMap.set(slug, {
    total: cur.total + (patch.total ?? 0),
    today: cur.today + (patch.today ?? 0),
    shares: cur.shares + (patch.shares ?? 0),
    referrals: cur.referrals + (patch.referrals ?? 0),
    predictions: cur.predictions + (patch.predictions ?? 0),
    views: cur.views + (patch.views ?? 0),
  });
}

function pushFeed(entry: Omit<FeedEntry, "id" | "at">) {
  const now = Date.now();
  if (now - lastFeedAt < FEED_MIN_GAP_MS) return;
  lastFeedAt = now;
  feed.unshift({ id: `${now}-${Math.random().toString(36).slice(2, 7)}`, at: now, ...entry });
  if (feed.length > FEED_MAX) feed.length = FEED_MAX;
}

function handle(eventType: string, data: Record<string, unknown>) {
  const country = (data.country as string | undefined)?.toUpperCase();
  // Only count events explicitly attributed to IN.
  if (country !== "IN") return;
  const slug = (data.influencerSlug as string | undefined) ?? (data.slug as string | undefined);

  switch (eventType) {
    case "first_prediction_completed":
      predictionsTotal += 1;
      pushFeed({ kind: "predict", message: "🎯 A new prediction was just submitted from India" });
      break;
    case "global_share_clicked":
    case "india_share_center_item_clicked": {
      sharesTotal += 1;
      const tpl = (data.template as string | undefined) ?? (data.kind as string | undefined) ?? "prediction";
      if (tpl in shareCounts) shareCounts[tpl as ShareItem["kind"]].count += 1;
      else shareCounts.prediction.count += 1;
      pushFeed({ kind: "share", message: "🔥 An India fan just shared on WhatsApp" });
      break;
    }
    case "whatsapp_growth_cta_clicked":
    case "telegram_channel_cta_clicked":
      referralsTotal += 1;
      break;
    case "influencer_page_view":
      if (slug) bumpInfluencer(slug, { total: 1, today: 1, views: 1 });
      break;
    case "influencer_share":
      if (slug) { bumpInfluencer(slug, { total: 1, today: 1, shares: 1 }); shareCounts.influencer.count += 1; sharesTotal += 1; }
      break;
    case "influencer_signup":
    case "influencer_referral":
      if (slug) { bumpInfluencer(slug, { total: 1, today: 1, referrals: 1 }); }
      communityTotal += 1; communityToday += 1; communityWeek += 1;
      pushFeed({ kind: "join", message: "🇮🇳 A new fan joined the India community" });
      break;
    case "influencer_prediction":
      if (slug) bumpInfluencer(slug, { total: 1, today: 1, predictions: 1 });
      break;
  }
  notify();
}

export function initIndiaMetrics(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.addEventListener("vs:event", ((e: CustomEvent<{ event_type?: string; event_data?: Record<string, unknown> }>) => {
    const det = e.detail;
    if (!det?.event_type) return;
    handle(det.event_type, det.event_data ?? {});
  }) as EventListener);
}

export function subscribeIndiaMetrics(l: Listener): () => void {
  initIndiaMetrics();
  listeners.add(l);
  return () => listeners.delete(l);
}

export function getIndiaInfluencerMetrics(slug: string): InfluencerCounts {
  return influencerMap.get(slug) ?? { total: 0, today: 0, shares: 0, referrals: 0, predictions: 0, views: 0 };
}

export function getIndiaCommunityCounters() {
  return { total: communityTotal, today: communityToday, week: communityWeek };
}

export function getIndiaShareLeaderboard(): ShareItem[] {
  return Object.values(shareCounts).sort((a, b) => b.count - a.count);
}

export function getIndiaCommunityFeed(): FeedEntry[] {
  return feed.slice(0, 12);
}

/**
 * 0-100 domination score, weighted across the six India pillars.
 * Same weighting model as Brasil to keep cross-country comparisons honest.
 */
export function computeIndiaDominationScore(): {
  score: number;
  status: "green" | "yellow" | "red";
  pillars: Array<{ key: string; label: string; value: number }>;
} {
  const infTotal = Array.from(influencerMap.values()).reduce((s, c) => s + c.total, 0);
  const usersFactor      = Math.min(20, communityTotal * 2);
  const retentionFactor  = Math.min(15, communityWeek);
  const viralityFactor   = Math.min(20, sharesTotal * 2);
  const influencerFactor = Math.min(15, infTotal);
  const predictionsFactor= Math.min(20, predictionsTotal * 2);
  const referralsFactor  = Math.min(10, referralsTotal);
  const score = usersFactor + retentionFactor + viralityFactor + influencerFactor + predictionsFactor + referralsFactor;
  const status: "green" | "yellow" | "red" = score >= 60 ? "green" : score >= 25 ? "yellow" : "red";
  return {
    score,
    status,
    pillars: [
      { key: "users",       label: "Users",       value: usersFactor },
      { key: "retention",   label: "Retention",   value: retentionFactor },
      { key: "virality",    label: "Virality",    value: viralityFactor },
      { key: "influencers", label: "Influencers", value: influencerFactor },
      { key: "predictions", label: "Predictions", value: predictionsFactor },
      { key: "referrals",   label: "Referrals",   value: referralsFactor },
    ],
  };
}